import { AppBar, makeStyles, Tab, Tabs } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link, useParams, Route, Switch } from 'react-router-dom';
import { useConfigObject } from './contentConfig.js';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import { IsExcludedBasedOnTypeOrProject } from '../../Utils/utils.js';

import { Error404 } from '../../components/Error404';
import { permissionCategory } from '../../Utils/access_rights';
import { hasRequiredSettings } from '../../Utils/update_values_utils';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
    backgroundColor: theme.palette.background.paper
  },
  contentContainer: {
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    }
  },
  tabContent: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2)
    }
  }
}));

function MissingCategory(tab_id, parent) {
  const missingCategories = ['nutrition', 'irrigation', 'scheduling'];
  if (tab_id === 'scheduling' && !('scheduling' in parent.settings)) {
    return true;
  }
  for (const id of missingCategories) {
    if (tab_id === id && !(id in parent.settings)) return true;
  }
  return false;
}

export function SettingsTabs(props) {
  let { tab_id = 'info' } = useParams();
  let { t } = useTranslation();
  const tabs = useConfigObject(props.controller, props.recoveryMode).filter(
    (tab) =>
      !IsExcludedBasedOnTypeOrProject(props.controller, tab.id, 'tabs') &&
      (props.controller.access_rights[permissionCategory(tab.id)] ?? {
        read: permissionCategory(tab.id) !== 'multi_io'
      }).read === true
  );
  const classes = useStyles();
  const visibleTabs = props.recoveryMode ? tabs.filter((tab) => tab.id === 'system') : tabs;
  const tab_index = visibleTabs.findIndex((tab) => tab.id === tab_id);

  return (
    <>
      <div className={classes.root}>
        <AppBar position="static" color="default">
          <Box display="flex" justifyContent="center" width="100%">
            <Tabs
              value={tab_index}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="controller settings tabs"
            >
              {visibleTabs.map((tab, index) => (
                <Tab
                  label={t(tab.id)}
                  component={Link}
                  to={`${props.url}/${tab.id}`}
                  key={`controller-tab-${tab.id}`}
                  id={`controller-tab-${index}`}
                  aria-controls={`controller-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>
        </AppBar>
      </div>

      <Switch>
        {hasRequiredSettings(props.parent?.settings, props.controller)
          ? tabs?.map((item) => (
              <Route key={`tab-route-${item.id}`} path={`${props.path}/:tab_id(${item.id})`}>
                <Container className={classes.contentContainer}>
                  <SettingsTabPanel
                    id={item.id}
                    parent={props.parent}
                    tabs={tabs}
                    contentClassName={classes.tabContent}
                  >
                    {item.content}
                  </SettingsTabPanel>
                </Container>
              </Route>
            ))
          : props.recoveryMode &&
            tabs
              .filter((tab) => tab.id === 'system')
              .map((item) => (
                <Route key={`recovery-tab-route-${item.id}`} path={`${props.path}/:tab_id?`}>
                  <Container className={classes.contentContainer}>
                    <SettingsTabPanel
                      id={item.id}
                      parent={props.parent}
                      tabs={tabs}
                      contentClassName={classes.tabContent}
                    >
                      {item.content}
                    </SettingsTabPanel>
                  </Container>
                </Route>
              ))}

        <Route key="settings-fallback-route" path="*">
          <Error404 />
        </Route>
      </Switch>
    </>
  );
}

function SettingsTabPanel({ id, parent, children, tabs, contentClassName, ...other }) {
  let { tab_id } = useParams();
  let { t } = useTranslation();
  if (id === 'info' && tab_id === undefined) tab_id = id;

  return (
    <div
      role="tabpanel"
      hidden={tab_id !== id}
      id={`wrapped-tabpanel-${id}`}
      aria-labelledby={`wrapped-tab-${id}`}
      {...other}
    >
      {tab_id === id && !MissingCategory(id, parent) && (
        <Box className={contentClassName}>{children}</Box>
      )}
      {tab_id === id && MissingCategory(id, parent) && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
          {t('update_controller_settings')}
        </div>
      )}
    </div>
  );
}
